import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk, RootState } from "../store";
import { fetchApi, fetchRaw } from "./api";
import {
  AncillariesState,
  EntityType,
  ApiEntityRequestParams,
  CommodityWithGroupType,
} from "./types";

const initialState: AncillariesState = {
  data: {
    terms: undefined,
    offices: undefined,
    vessels: undefined,
    terminals: undefined,
    commodities: undefined,
    warehouses: undefined,
    commodityWithGroup: undefined,
  },
  loading: false,
  error: null,
};

const ENDPOINTS = {
  offices: "/api/offices",
  commodities: "/api/commodities",
  terms: "/api/terms-of-payment",
  terminals: "/api/terminals",
  vessels: "/api/vessels",
  warehouses: "/api/warehouses",
  customerAddresses: "/api/customer-addresses",
  commodityWithGroup: "/api/commodities/parents",
} as const;

const ancillaries = createSlice({
  name: "ancillaries",
  initialState: initialState,
  reducers: {
    getFetchingStart(state: AncillariesState) {
      state.loading = true;
      state.error = null;
    },
    getFetchingSuccess<T>(
      state: AncillariesState,
      action: PayloadAction<{ type: string; items: T[]; total?: number }>,
    ) {
      state.data[action.payload.type] = {
        items: action.payload.items,
        total: action.payload.total as number,
      };
      state.loading = false;
    },
    getFetchingFailure(state: AncillariesState, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const { getFetchingStart, getFetchingSuccess, getFetchingFailure } =
  ancillaries.actions;

export default ancillaries.reducer;

const selectAncillaries = (state: RootState) => state.ancillaries;

export const selectCommodities = createSelector(
  [selectAncillaries],
  (a) => a.data.commodities || null,
);

export const selectCommoditiesWithGroup = createSelector(
  [selectAncillaries],
  (a) => a.data.commodityWithGroup?.items || null,
);

export function createAncillaryThunk<K extends keyof typeof ENDPOINTS, T>(
  key: K,
) {
  return (params?: ApiEntityRequestParams): AppThunk =>
    async (dispatch) => {
      try {
        dispatch(getFetchingStart());
        const { items, total } = await fetchApi<T>(ENDPOINTS[key], params);
        dispatch(getFetchingSuccess({ type: key, items, total }));
      } catch (err) {
        const error = err instanceof Error ? err.message : String(err);
        dispatch(getFetchingFailure(error));
      }
    };
}

export const fetchCommodityWithGroup = (): AppThunk => async (dispatch) => {
  try {
    dispatch(getFetchingStart());

    const data = await fetchRaw<CommodityWithGroupType[]>(
      "/api/commodities/parents",
    );

    dispatch(
      getFetchingSuccess({
        type: "commodityWithGroup",
        items: data,
      }),
    );
  } catch (err) {
    const error = err instanceof Error ? err.message : String(err);
    dispatch(getFetchingFailure(error));
  }
};

export const fetchOffices = createAncillaryThunk<"offices", EntityType>(
  "offices",
);
export const fetchCommodities = createAncillaryThunk<"commodities", EntityType>(
  "commodities",
);
export const fetchTerms = createAncillaryThunk<"terms", EntityType>("terms");
export const fetchTerminals = createAncillaryThunk<"terminals", EntityType>(
  "terminals",
);
export const fetchWarehouses = createAncillaryThunk<"warehouses", EntityType>(
  "warehouses",
);
